import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type SpeciesCategory = 'Mammal' | 'Bird';
type SpeciesKey =
  | 'Elg'
  | 'Hjort'
  | 'Radyr'
  | 'Rein'
  | 'Rev'
  | 'Grevling'
  | 'Oter'
  | 'Bever'
  | 'Havorn'
  | 'Kongeorn'
  | 'Ugle'
  | 'Tiur'
  | 'Orrfugl'
  | 'Trane';

export interface WildlifeSpeciesCount {
  species: string;
  count: number;
  category: SpeciesCategory;
  lastSeen: Date | null;
}

interface WildlifeResult {
  species: WildlifeSpeciesCount[];
  latestSeen: Date | null;
  loading: boolean;
  error: string | null;
}

interface CacheEntry {
  ts: number;
  data: WildlifeSpeciesCount[];
}

const CACHE_TTL_MS = 30 * 60 * 1000;
const cache = new Map<string, CacheEntry>();
const PROXY_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy';
const WILDLIFE_RADIUS_METERS = 2000; // Keep in sync with hiking badge radius.

const SPECIES_META: Record<SpeciesKey, { label: string; category: SpeciesCategory; emoji: string }> = {
  Elg: { label: 'Elg', category: 'Mammal', emoji: '🦌' },
  Hjort: { label: 'Hjort', category: 'Mammal', emoji: '🦌' },
  Radyr: { label: 'Radyr', category: 'Mammal', emoji: '🦌' },
  Rein: { label: 'Rein', category: 'Mammal', emoji: '🦌' },
  Rev: { label: 'Rev', category: 'Mammal', emoji: '🦊' },
  Grevling: { label: 'Grevling', category: 'Mammal', emoji: '🦡' },
  Oter: { label: 'Oter', category: 'Mammal', emoji: '🦦' },
  Bever: { label: 'Bever', category: 'Mammal', emoji: '🦫' },
  Havorn: { label: 'Havorn', category: 'Bird', emoji: '🦅' },
  Kongeorn: { label: 'Kongeorn', category: 'Bird', emoji: '🦅' },
  Ugle: { label: 'Ugle', category: 'Bird', emoji: '🦉' },
  Tiur: { label: 'Tiur', category: 'Bird', emoji: '🐦' },
  Orrfugl: { label: 'Orrfugl', category: 'Bird', emoji: '🐦' },
  Trane: { label: 'Trane', category: 'Bird', emoji: '🕊️' }
};

const SCIENTIFIC_TO_KEY: Record<string, SpeciesKey> = {
  'alces alces': 'Elg',
  'cervus elaphus': 'Hjort',
  'capreolus capreolus': 'Radyr',
  'rangifer tarandus': 'Rein',
  'vulpes vulpes': 'Rev',
  'meles meles': 'Grevling',
  'lutra lutra': 'Oter',
  'castor fiber': 'Bever',
  'haliaeetus albicilla': 'Havorn',
  'aquila chrysaetos': 'Kongeorn',
  'tetrao urogallus': 'Tiur',
  'lyrurus tetrix': 'Orrfugl',
  'grus grus': 'Trane',
  strigidae: 'Ugle'
};

const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const extractNameCandidates = (obs: any): string[] => {
  const taxon = obs?.taxon || obs?.Taxon || {};
  const names = [
    obs?.vernacularName,
    obs?.vernacularNameNb,
    obs?.vernacularNameBokmal,
    obs?.taxonVernacularName,
    obs?.taxonName,
    obs?.speciesName,
    obs?.name,
    obs?.scientificName,
    taxon?.vernacularName,
    taxon?.vernacularNameNb,
    taxon?.vernacularNameBokmal,
    taxon?.scientificName,
    taxon?.name
  ].filter(Boolean);
  return names.map((v: any) => String(v));
};

const mapObservationToSpeciesKey = (obs: any): SpeciesKey | null => {
  const names = extractNameCandidates(obs).map(normalize);

  for (const name of names) {
    if (name.includes('elg')) return 'Elg';
    if (name.includes('hjort')) return 'Hjort';
    if (name.includes('radyr') || name.includes('rådyr')) return 'Radyr';
    if (name.includes('rein')) return 'Rein';
    if (name.includes('rev')) return 'Rev';
    if (name.includes('grevling')) return 'Grevling';
    if (name.includes('oter') || name.includes('otter')) return 'Oter';
    if (name.includes('bever')) return 'Bever';
    if (name.includes('havorn') || name.includes('havørn')) return 'Havorn';
    if (name.includes('kongeorn') || name.includes('kongeørn')) return 'Kongeorn';
    if (name.includes('ugle') || name.includes('ugler')) return 'Ugle';
    if (name.includes('tiur')) return 'Tiur';
    if (name.includes('orrfugl')) return 'Orrfugl';
    if (name.includes('trane') || name.includes('traner')) return 'Trane';
  }

  for (const name of names) {
    if (SCIENTIFIC_TO_KEY[name]) return SCIENTIFIC_TO_KEY[name];
  }

  return null;
};

const parseObservationDate = (obs: any): Date | null => {
  const raw =
    obs?.observedAt ||
    obs?.observationDate ||
    obs?.eventDate ||
    obs?.dateObserved ||
    obs?.createdAt ||
    obs?.date ||
    obs?.TrackDateTime;

  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateISO = (date: Date): string => date.toISOString().slice(0, 10);

const toWebMercator = (lat: number, lon: number): { x: number; y: number } => {
  const r = 6378137;
  return {
    x: (lon * Math.PI / 180) * r,
    y: Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 360))) * r
  };
};

const buildGmWktPolygon = (lat: number, lon: number, radiusMeters: number): string => {
  const m = toWebMercator(lat, lon);
  const x1 = m.x - radiusMeters;
  const x2 = m.x + radiusMeters;
  const y1 = m.y - radiusMeters;
  const y2 = m.y + radiusMeters;
  return `POLYGON((${x1} ${y1},${x2} ${y1},${x2} ${y2},${x1} ${y2},${x1} ${y1}))`;
};

const buildFetchUrl = (lat: number, lon: number): string => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 12);

  const params = new URLSearchParams();
  params.set('pageIndex', '0');
  params.set('pageSize', '500');
  params.set('fromDate', formatDateISO(from));
  params.set('toDate', formatDateISO(to));
  params.set('gmWktPolygon', buildGmWktPolygon(lat, lon, WILDLIFE_RADIUS_METERS));

  const sourceUrl = `https://artskart.artsdatabanken.no/publicapi/api/observations/list?${params.toString()}`;
  return `${PROXY_URL}?url=${encodeURIComponent(sourceUrl)}`;
};

export const getSpeciesEmoji = (speciesKey: string): string => {
  return SPECIES_META[speciesKey as SpeciesKey]?.emoji || '🐾';
};

export const getRelativeTime = (date: Date | null, t: any): string => {
  if (!date) return t('natureScout.no_time_data');

  const now = Date.now();
  const diffMs = now - date.getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const days = Math.floor(diffMs / dayMs);

  if (days <= 0) return t('natureScout.time.today');
  if (days === 1) return t('natureScout.time.yesterday');
  if (days < 7) return t('natureScout.time.days_ago', { count: days });
  if (days < 30) return t('natureScout.time.weeks_ago', { count: Math.floor(days / 7) });
  if (days < 365) return t('natureScout.time.months_ago', { count: Math.floor(days / 30) });
  return t('natureScout.time.years_ago', { count: Math.floor(days / 365) });
};

export const useWildlife = (lat?: number, lon?: number): WildlifeResult => {
  const { t } = useTranslation();
  const [species, setSpecies] = useState<WildlifeSpeciesCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof lat !== 'number' || typeof lon !== 'number') {
      setSpecies([]);
      return;
    }

    const key = `${lat.toFixed(3)},${lon.toFixed(3)}`;
    const cached = cache.get(key);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      setSpecies(cached.data);
      return;
    }

    const controller = new AbortController();

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(buildFetchUrl(lat, lon), {
          signal: controller.signal,
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) throw new Error(`wildlife_fetch_failed_${response.status}`);
        const json = await response.json();
        const rawList: any[] = Array.isArray(json)
          ? json
          : Array.isArray(json?.data)
            ? json.data
            : Array.isArray(json?.results)
              ? json.results
              : Array.isArray(json?.observations)
                ? json.observations
                : [];

        const grouped = new Map<SpeciesKey, { count: number; lastSeen: Date | null }>();
        rawList.forEach((obs) => {
          const key = mapObservationToSpeciesKey(obs);
          if (!key) return;
          const seenAt = parseObservationDate(obs);
          const prev = grouped.get(key);

          if (!prev) {
            grouped.set(key, { count: 1, lastSeen: seenAt });
            return;
          }

          const newer =
            prev.lastSeen && seenAt
              ? seenAt.getTime() > prev.lastSeen.getTime()
                ? seenAt
                : prev.lastSeen
              : prev.lastSeen || seenAt || null;

          grouped.set(key, { count: prev.count + 1, lastSeen: newer });
        });

        const speciesList: WildlifeSpeciesCount[] = Array.from(grouped.entries())
          .map(([speciesKey, payload]) => ({
            species: speciesKey,
            count: payload.count,
            category: SPECIES_META[speciesKey].category,
            lastSeen: payload.lastSeen
          }))
          .sort((a, b) => {
            const ad = a.lastSeen ? a.lastSeen.getTime() : 0;
            const bd = b.lastSeen ? b.lastSeen.getTime() : 0;
            if (bd !== ad) return bd - ad;
            return b.count - a.count;
          });

        setSpecies(speciesList);
        cache.set(key, { ts: Date.now(), data: speciesList });
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setError(t('natureScout.error'));
        setSpecies([]);
      } finally {
        setLoading(false);
      }
    };

    run();
    return () => controller.abort();
  }, [lat, lon, t]);

  const latestSeen = useMemo(() => {
    if (species.length === 0) return null;
    return species.reduce<Date | null>((acc, item) => {
      if (!item.lastSeen) return acc;
      if (!acc) return item.lastSeen;
      return item.lastSeen.getTime() > acc.getTime() ? item.lastSeen : acc;
    }, null);
  }, [species]);

  return { species, latestSeen, loading, error };
};
